/**
 * Lecture d'un QR code dans le flux de la camera.
 *
 * Deux decodeurs, choisis a l'execution :
 *
 * - `BarcodeDetector`, l'API du navigateur, quand elle existe et sait lire le
 *   QR code (Chrome et Edge sur Android) ;
 * - `jsqr` sinon : Safari, sur iPhone comme sur Mac, n'expose pas cette API.
 *   Le paquet n'est charge qu'a ce moment-la, pour ne pas alourdir le menu.
 *
 * Voir docs/adr/0031.
 */

import { QR_CLIENT_PATH_PREFIX } from '@scan2eat/shared';

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorConstructor {
  new (opts?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorConstructor;
  }
}

/** Lit l'image courante de la video ; `null` si aucun QR code n'y figure. */
export type QrDecoder = (video: HTMLVideoElement) => Promise<string | null>;

/** Largeur maximale de l'image analysee par jsqr : au-dela, le gain est nul et le cout eleve. */
const MAX_DECODE_WIDTH = 640;

async function nativeDecoder(): Promise<QrDecoder | null> {
  const Detector = window.BarcodeDetector;
  if (!Detector) return null;
  try {
    const formats = (await Detector.getSupportedFormats?.()) ?? ['qr_code'];
    if (!formats.includes('qr_code')) return null;
    const detector = new Detector({ formats: ['qr_code'] });
    return async (video) => (await detector.detect(video))[0]?.rawValue ?? null;
  } catch {
    return null;
  }
}

async function jsqrDecoder(): Promise<QrDecoder> {
  const { default: jsQR } = await import('jsqr');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });

  return async (video) => {
    const { videoWidth, videoHeight } = video;
    if (!context || !videoWidth || !videoHeight) return null;
    const scale = Math.min(1, MAX_DECODE_WIDTH / videoWidth);
    const width = Math.round(videoWidth * scale);
    const height = Math.round(videoHeight * scale);
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    context.drawImage(video, 0, 0, width, height);
    const image = context.getImageData(0, 0, width, height);
    return jsQR(image.data, width, height, { inversionAttempts: 'dontInvert' })?.data ?? null;
  };
}

/** Meilleur decodeur disponible sur ce navigateur. */
export async function createQrDecoder(): Promise<QrDecoder> {
  return (await nativeDecoder()) ?? jsqrDecoder();
}

/** Cause d'un echec d'ouverture de la camera, pour choisir le message affiche. */
export type CameraFailure = 'denied' | 'insecure' | 'unsupported' | 'not-found' | 'busy' | 'unknown';

/** `getUserMedia` n'existe que sur une page servie en https (ou localhost). */
export function cameraAvailability(): CameraFailure | null {
  if (!window.isSecureContext) return 'insecure';
  if (!navigator.mediaDevices?.getUserMedia) return 'unsupported';
  return null;
}

/** Traduit l'erreur de `getUserMedia` en cause lisible. */
export function classifyCameraError(error: unknown): CameraFailure {
  const name = error instanceof DOMException || error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'not-found';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy';
    default:
      return 'unknown';
  }
}

/** Ouvre la camera arriere, a defaut n'importe laquelle. */
export function openRearCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
  });
}

/** Extrait le jeton d'un QR code de table, lien complet ou jeton brut. */
export function extractQrToken(input: string): string | null {
  const value = input.trim();
  if (!value) return null;
  const idx = value.indexOf(QR_CLIENT_PATH_PREFIX);
  if (idx !== -1) {
    const token = value.slice(idx + QR_CLIENT_PATH_PREFIX.length).split(/[?#/]/)[0];
    return token || null;
  }
  return value;
}
